import { FC, useCallback, useMemo, useState } from 'react';
import { Group, Title, Box } from '@mantine/core';
import { IconLayoutKanban } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useNavigate } from 'react-router-dom';
import { ResourceSearchForm } from '@/components/resource-search/ResourceSearchForm';
import {
  ORDER_SEARCH_DEFAULTS,
  OrderSearchParams,
  getOrderSearchFields,
} from './orderSearchConfig';
import { useOrderSearchForm } from '../hooks/useOrderSearchForm';
import { OrdersKanbanBoard } from './OrdersKanbanBoard';
import { KanbanSharedParams } from '../hooks/useKanbanColumn';
import classes from './OrdersKanbanPage.module.css';

export const OrdersKanbanPage: FC = () => {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { t } = useTranslation('lims');

  // Track the shared params that get passed to every column
  const [sharedParams, setSharedParams] = useState<KanbanSharedParams>({});

  const {
    searchParams,
    updateSearchParams,
    clearSearch,
    patientNameQuery,
    setPatientNameQuery,
    setPatientQuery,
    patientNameOptions,
    patientLoading,
    patientNameLoading,
    resourceIdQuery: orderIdQuery,
    setResourceIdQuery: setOrderIdQuery,
    resourceIdOptions: orderIdOptions,
    resourceIdLoading: orderIdLoading,
  } = useOrderSearchForm(medplum);

  const handleSearch = useCallback(
    (updates: Partial<OrderSearchParams>) => {
      updateSearchParams(updates);
      const merged = { ...searchParams, ...updates };
      // Strip subState — the board organises by substate itself
      const { subState: _ignored, ...shared } = merged;
      setSharedParams(shared);
    },
    [searchParams, updateSearchParams]
  );

  const handleClearSearch = useCallback(() => {
    clearSearch(ORDER_SEARCH_DEFAULTS);
    setSharedParams({});
  }, [clearSearch]);

  const handleOrderClick = useCallback(
    (order: ServiceRequest) => {
      if (order.id) {
        navigate(`/lims/order/${order.id}`);
      }
    },
    [navigate]
  );

  // Same fields as OrdersList but with subState removed (columns are the substates)
  const fields = useMemo(
    () =>
      getOrderSearchFields({
        t,
        searchParams,
        onSearch: handleSearch,
        hidePatientName: false,
        patientNameQuery,
        setPatientNameQuery,
        setPatientQuery,
        patientNameOptions,
        patientLoading,
        patientNameLoading,
        orderIdQuery,
        setOrderIdQuery,
        orderIdOptions,
        orderIdLoading,
      }).filter((f) => f.key !== 'subState'),
    [
      handleSearch,
      orderIdLoading,
      orderIdOptions,
      orderIdQuery,
      patientLoading,
      patientNameLoading,
      patientNameOptions,
      patientNameQuery,
      searchParams,
      setOrderIdQuery,
      setPatientNameQuery,
      setPatientQuery,
      t,
    ]
  );

  return (
    <Box className={classes.page}>
      {/* Header bar */}
      <Box className={classes.header}>
        <Group justify="space-between" mb={12}>
          <Group gap="sm">
            <IconLayoutKanban size={20} />
            <Title order={3}>{t('kanban.title')}</Title>
          </Group>
        </Group>

        <ResourceSearchForm<OrderSearchParams>
          fields={fields}
          searchParams={searchParams}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          clearLabel={t('orderList.clear', { ns: 'lims' })}
          layout={{ cols: { base: 1, sm: 2, lg: 4 } }}
        />
      </Box>

      {/* Board — fills remaining space, only horizontal scroll + per-column vertical */}
      <Box className={classes.board}>
        <OrdersKanbanBoard
          medplum={medplum}
          sharedParams={sharedParams}
          onOrderClick={handleOrderClick}
        />
      </Box>
    </Box>
  );
};
